import { BoundaryPartRequestGeometryType } from 'generatedTypes/flight/models';
import { Marker } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ADDRESS_SEARCH_ZOOM } from 'components/Map/constants';
import useBoundary from 'components/Map/hooks/useBoundary';
import useBoundaryReset from 'components/Map/hooks/useBoundaryReset';
import useClick from 'components/Map/hooks/useClick';
import useDTO from 'components/Map/hooks/useDTO';
import useDrawingTool from 'components/Map/hooks/useDrawingTool';
import useFlightPath from 'components/Map/hooks/useFlightPaths';
import useLayerStyles from 'components/Map/hooks/useLayerStyles';
import useLayerVisibility from 'components/Map/hooks/useLayerVisibility';
import useMapContext from 'components/Map/hooks/useMapContext';
import useMapRecenter from 'components/Map/hooks/useMapRecenter';
import useReticle from 'components/Map/hooks/useReticle';
import useSelectFeature from 'components/Map/hooks/useSelectFeature';
import useStaticGeometry from 'components/Map/hooks/useStaticGeometry';
import useTranslation from 'components/Map/hooks/useTranslation';
import MapAddressSearch from 'components/MapAddressSearch';
import BoundaryPopover from 'components/MapBoundaryPopover';
import MapBox from 'components/MapBox';
import MapControlPopup from 'components/MapControlPopup';
import MapControlResetPopup from 'components/MapControlResetPopup';
import MapHorizontalControlBar from 'components/MapHorizontalControlBar';
import MapVerticalControlBar from 'components/MapVerticalControlBar';

import {
    EMPTY_PARTS,
    GEOJSON_PROPERTY,
    SINGLE_BOUNDARY_COUNT,
    STATIC_LAYER_IDS,
    STATIC_ZOOM,
    TEST_ID
} from './constants';
import type {
    CanvasPosition,
    Feature,
    LngLat,
    Map,
    MapBoxConfig,
    Polygon,
    Position,
    Props,
    SelectAddressParams,
    VertexPart
} from './types';

import styles from './styles.module.scss';

const MapInternal = ({
    className = '',
    flightPaths,
    focusGeometry,
    geometry,
    mapInteractionOptions,
    mapOptions,
    onClickEditMap,
    onClose,
    onComplete,
    onFeatureSelect,
    selectedFlight,
    testId = TEST_ID
}: Props) => {
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const searchMarkerRef = useRef<Marker | null>(null);

    const [map, setMap] = useState<Map>();
    const [boundaryOptionPopoverOpen, setBoundaryOptionPopoverOpen] = useState(false);
    const [boundaryPopoverPosition, setBoundaryPopoverPosition] = useState<{
        x: number;
        y: number;
    }>();
    const [isDrawingInfoPopupOpen, setIsDrawingInfoPopupOpen] = useState(false);

    const { INCOMPLETE_BOUNDARY } = useTranslation();

    const { isEditable, parts } = geometry ?? {};
    const staticGeometryParts = isEditable ? EMPTY_PARTS : parts;
    const editableGeometryParts = isEditable ? parts : [];

    const vertexCoords = useMemo<VertexPart[]>(
        () =>
            (parts ?? [])
                .map((part, partIndex) => ({
                    coords: (part.boundary?.coordinates?.[0] ?? []) as Position[],
                    geometryType: part.geometryType,
                    id: `part-${partIndex}`,
                    partIndex
                }))
                .filter(part => part.geometryType !== BoundaryPartRequestGeometryType.CIRCLE)
                .map(({ coords, id, partIndex }) => ({ coords, id, partIndex })),
        [parts]
    );

    const {
        hasAddressSearch = false,
        hasDrawingControls = false,
        hasExitConfirmation = false,
        hasLayerToggles = false,
        hasMapControls = false
    } = mapInteractionOptions;

    const { features, mapState, markers } = useMapContext();

    const { clear: clearFeatures } = features;
    const { clear: clearMarkers, markerMap, refreshInteractivity } = markers;

    const {
        drawingTool,
        isDrawing,
        pointMode,
        selectedFeature,
        setMapCenter,
        setMapErrors,
        setMousePosition,
        setSelectedFeature
    } = mapState;

    const { boundaryPolygon, noEntryPolygons, removeBoundaryForFeature, updateBoundaryForFeature } =
        useBoundary({ isInteractive: hasDrawingControls });

    useEffect(() => {
        refreshInteractivity(hasDrawingControls);
    }, [hasDrawingControls, refreshInteractivity]);

    const { convertToDTO } = useDTO();

    const {
        getClusterPointStyle,
        getDrawingStyles,
        getStaticGeometryStyles,
        getUnclusteredPointStyle
    } = useLayerStyles();

    const { initializeStaticGeometry } = useStaticGeometry();
    const { highlightFlight, initializeFlightPaths } = useFlightPath();
    const { isBoundaryVisible, isFlightPathsVisible, toggleBoundary, toggleFlightPaths } =
        useLayerVisibility();

    const hasFlightPaths = (flightPaths?.length ?? 0) > 0;
    const hasBoundary = (staticGeometryParts?.length ?? 0) > 0;

    useEffect(() => {
        clearMarkers();
        clearFeatures();

        setSelectedFeature(undefined);
        setMapErrors([]);

        resetDrawingState();

        if (staticGeometryParts) {
            initializeStaticGeometry(staticGeometryParts);
        }

        if (editableGeometryParts) {
            loadExternalGeometry(editableGeometryParts);
        }

        initializeFlightPaths(flightPaths);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditable, parts]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const isSingleBoundary = (staticGeometryParts?.length ?? 0) <= SINGLE_BOUNDARY_COUNT;
        const minZoom = isSingleBoundary ? 0 : STATIC_ZOOM.min;

        for (const layerId of STATIC_LAYER_IDS) {
            if (map.getLayer(layerId)) {
                map.setLayerZoomRange(layerId, minZoom, STATIC_ZOOM.max);
            }
        }
    }, [map, staticGeometryParts]);

    useEffect(() => {
        initializeFlightPaths(flightPaths);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flightPaths]);

    useEffect(() => {
        highlightFlight(selectedFlight);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlight]);

    const {
        clearUndoRedo,
        handleClick,
        handleCompleteDrawing,
        handleLoadDrawing,
        handleResetDrawing,
        loadExternalGeometry,
        redo,
        resetDrawingState,
        undo
    } = useDrawingTool({
        onDrawingCompleted: feature => {
            handleDrawingCompleted(feature);
        },
        onDrawingUpdated: feature => {
            handleDrawingUpdated(feature);
        }
    });

    useReticle();

    useSelectFeature({
        map,
        onDrawingComplete: handleCompleteDrawing,
        onDrawingLoaded: handleLoadDrawing
    });

    const { handleMapClick } = useClick({
        boundaryPolygon,
        isDrawing,
        noEntryPolygons,
        onClick: handleClick,
        onClickBoundaryLabel: position => {
            setBoundaryOptionPopoverOpen(true);

            const projected = map?.project(position as [number, number]);

            setBoundaryPopoverPosition(projected);
        }
    });

    const { centerOnVisibleArtifacts, handleChangeMapCenter } = useMapRecenter({
        flightPaths,
        focusGeometry,
        geometry,
        isBoundaryVisible,
        isFlightPathsVisible,
        map,
        mapOptions
    });

    const handleToggleFlightPaths = () => {
        const newVisible = toggleFlightPaths(map);

        if (newVisible) {
            centerOnVisibleArtifacts({ isBoundaryVisible, isFlightPathsVisible: newVisible });
        }
    };

    const handleToggleBoundary = () => {
        const newVisible = toggleBoundary(map);

        if (newVisible) {
            centerOnVisibleArtifacts({ isBoundaryVisible: newVisible, isFlightPathsVisible });
        }
    };

    const handleClearDrawing = () => {
        handleResetDrawing();
        setMapErrors([]);

        if (selectedFeature) {
            features.removeFeature(selectedFeature.id as string);
            setSelectedFeature(undefined);

            removeBoundaryForFeature(selectedFeature.id as string);

            return;
        }
    };

    const handleCompleteClicked = () => {
        if (isDrawing) {
            handleCompleteDrawing();

            return;
        }

        if (!boundaryPolygon) {
            const incompleteBoundaryError = new Error(INCOMPLETE_BOUNDARY);

            setMapErrors([incompleteBoundaryError]);

            return;
        }

        const dto = convertToDTO(boundaryPolygon, noEntryPolygons);

        onComplete?.(dto);

        if (selectedFeature) {
            selectedFeature.properties = {
                ...selectedFeature.properties,
                [GEOJSON_PROPERTY.IS_SELECTED]: false
            };
            setSelectedFeature(undefined);
        }
    };

    const {
        handleDrawingModeChange,
        handleDrawingToolChange,
        handleExitDrawing,
        handlePointModeChange,
        isResetPopupOpen,
        resetState
    } = useBoundaryReset({
        hasExitConfirmation,
        onCompleteDrawing: handleCompleteClicked,
        onDrawingReset: handleClearDrawing,
        onExitDrawing: onClose
    });

    const handleDrawingCompleted = (feature: Feature<Polygon>) => {
        updateBoundaryForFeature(feature);

        searchMarkerRef.current?.remove();
        searchMarkerRef.current = null;

        if (selectedFeature?.id === feature.id) {
            handleLoadDrawing(feature);
        } else {
            clearUndoRedo();
        }
    };

    const handleDrawingUpdated = (feature: Feature<Polygon>) => {
        updateBoundaryForFeature(feature);
    };

    const getMapboxConfig = (): MapBoxConfig => {
        const { accessToken, center, mapStyle, maxZoom, minZoom, zoom } = mapOptions;

        return {
            accessToken,
            center,
            layerStyles: {
                getClusterStyle: getClusterPointStyle,
                getDrawingStyles: getDrawingStyles,
                getStaticGeometryStyles: getStaticGeometryStyles,
                getUnclusteredStyle: getUnclusteredPointStyle
            },
            mapStyle,
            maxZoom,
            minZoom,
            zoom
        };
    };

    useEffect(() => {
        setIsDrawingInfoPopupOpen(true);
    }, [pointMode, drawingTool]);

    const handleMapLoad = (loadedMap: Map) => {
        setMap(loadedMap);

        const mapContainer = loadedMap.getContainer();

        if (!mapContainer) {
            return;
        }

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                requestAnimationFrame(() => {
                    if (!loadedMap.getCanvas()) {
                        return;
                    }

                    loadedMap.resize();
                });
            });
        }

        resizeObserverRef.current.observe(mapContainer);
    };

    useEffect(
        () => () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }

            searchMarkerRef.current?.remove();
            searchMarkerRef.current = null;
        },
        []
    );

    const handleSelectAddress = ({ center }: SelectAddressParams) => {
        if (!map) {
            return;
        }

        const lngLat = center as LngLat;

        searchMarkerRef.current?.remove();
        searchMarkerRef.current = new Marker().setLngLat(lngLat).addTo(map);

        map.flyTo({
            center: lngLat,
            zoom: ADDRESS_SEARCH_ZOOM
        });
    };

    const handleClearAddress = () => {
        searchMarkerRef.current?.remove();
        searchMarkerRef.current = null;
    };

    const isMapControlPopupEnabled =
        hasDrawingControls && !isResetPopupOpen && isDrawingInfoPopupOpen;
    const isResetPopupEnabled = hasDrawingControls && isResetPopupOpen;

    return (
        <>
            <MapBox
                onClick={(markerId?: string) =>
                    handleMapClick({
                        hasDrawingControls,
                        map,
                        markerId
                    })
                }
                className={`${styles.mapbox} ${className}`}
                config={getMapboxConfig()}
                geojson={features.features}
                markers={Object.values(markerMap)}
                onDoubleClick={isDrawing ? handleCompleteClicked : undefined}
                onFeatureSelect={onFeatureSelect}
                onLoad={handleMapLoad}
                onMouseMove={setMousePosition}
                onMove={setMapCenter}
                selectedFlightId={selectedFlight?.id}
                testId={testId}
                vertexCoords={vertexCoords}>
                {boundaryOptionPopoverOpen && (
                    <BoundaryPopover
                        onClose={() => {
                            setBoundaryOptionPopoverOpen(false);
                            setBoundaryPopoverPosition(undefined);
                        }}
                        canvasPosition={boundaryPopoverPosition as CanvasPosition}
                        isOpen={boundaryOptionPopoverOpen}
                        onDelete={handleClearDrawing}
                    />
                )}
            </MapBox>

            {hasAddressSearch && (
                <MapAddressSearch
                    className={styles.addressSearch}
                    onClearAddress={handleClearAddress}
                    onSelectAddress={handleSelectAddress}
                />
            )}

            {hasDrawingControls && (
                <MapHorizontalControlBar
                    className={styles.horizontalControlBar}
                    onChangeDrawingMode={handleDrawingModeChange}
                    onChangeDrawingTool={handleDrawingToolChange}
                    onChangePointMode={handlePointModeChange}
                    onClickClose={handleExitDrawing}
                    onClickCompleteDrawing={handleCompleteClicked}
                    onClickRecenter={handleChangeMapCenter}
                    onClickRedo={redo}
                    onClickUndo={undo}
                />
            )}

            {hasMapControls && (
                <MapVerticalControlBar
                    layerToggles={
                        hasLayerToggles && !hasDrawingControls
                            ? {
                                  hasBoundary,
                                  hasFlightPaths,
                                  isBoundaryVisible,
                                  isFlightPathsVisible,
                                  onToggleBoundary: handleToggleBoundary,
                                  onToggleFlightPaths: handleToggleFlightPaths
                              }
                            : undefined
                    }
                    className={styles.verticalControlBar}
                    isDrawingEnabled={hasDrawingControls}
                    onClickEditMap={onClickEditMap}
                    onClickRecenterMap={handleChangeMapCenter}
                />
            )}

            {isMapControlPopupEnabled && (
                <MapControlPopup
                    onClickAddPoint={() => {
                        handleMapClick({
                            hasDrawingControls,
                            map
                        });
                    }}
                    onClickDismiss={() => {
                        setIsDrawingInfoPopupOpen(false);
                    }}
                />
            )}

            {isResetPopupEnabled && <MapControlResetPopup resetState={resetState} />}
        </>
    );
};

export default MapInternal;
